import { useState } from "react";
import { useAuth } from "@/App";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CommissionDetailDialog } from "./commissions/components/CommissionDetailDialog";
import { CommissionStatsGrid } from "./commissions/components/CommissionStatsGrid";
import { CommissionsContent } from "./commissions/components/CommissionsContent";
import { CommissionsPageIntro } from "./commissions/components/CommissionsPageIntro";
import { CommissionsToolbar } from "./commissions/components/CommissionsToolbar";
import {
  COMMISSIONS_PAGE_GUIDE,
  COMMISSIONS_PAGE_TIP,
} from "./commissions/commission.constants";
import { useCommissionPageActions } from "./commissions/hooks/useCommissionPageActions";
import { useCommissionsPageData } from "./commissions/hooks/useCommissionsPageData";
import { useCommissionsPageState } from "./commissions/hooks/useCommissionsPageState";
import { useAttributionReassignmentFlag } from "@/hooks/queries/useAttributionReassignmentFlag";
import { canUseReassignment } from "@/lib/reassignment";

export default function CommissionsPage() {
  const { user } = useAuth();
  const pageState = useCommissionsPageState();
  const pageData = useCommissionsPageData({
    typeFilter: pageState.typeFilter,
    search: pageState.search,
  });
  const actions = useCommissionPageActions();
  const canDelete = user?.role === "super_admin";
  const { data: reassignmentFlag } = useAttributionReassignmentFlag();
  const canReassign = canUseReassignment(user?.role, reassignmentFlag);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  usePageHeader({
    title: "Commissions",
    helpTopic: "commissions",
    guide: {
      steps: COMMISSIONS_PAGE_GUIDE,
      tip: COMMISSIONS_PAGE_TIP,
    },
  });

  return (
    <div>
      <CommissionsPageIntro onCreated={actions.reloadCommissions} />
      <CommissionStatsGrid
        totalCount={pageData.commissions.length}
        filteredCount={pageData.filteredCommissions.length}
        metrics={pageData.metrics}
      />
      <CommissionsToolbar
        search={pageState.search}
        typeFilter={pageState.typeFilter}
        viewMode={pageState.viewMode}
        types={pageData.types}
        filteredCount={pageData.filteredCommissions.length}
        onSearchChange={pageState.setSearch}
        onTypeFilterChange={pageState.setTypeFilter}
        onViewModeChange={pageState.setViewMode}
      />
      <CommissionsContent
        commissions={pageData.filteredCommissions}
        parties={pageData.parties}
        viewMode={pageState.viewMode}
        loading={pageData.isLoading}
        canDelete={canDelete}
        deletingId={actions.deletingId}
        typeLabel={pageData.typeLabel}
        onSelect={pageState.setSelectedCommission}
        onReload={actions.reloadCommissions}
        onDelete={setPendingDeleteId}
      />
      <CommissionDetailDialog
        commission={pageState.selectedCommission}
        parties={pageData.parties}
        canDelete={canDelete}
        canReassign={canReassign}
        deletingId={actions.deletingId}
        typeLabel={pageData.typeLabel}
        onOpenChange={(open) => {
          if (!open) pageState.setSelectedCommission(null);
        }}
        onReload={actions.reloadCommissions}
        onDelete={setPendingDeleteId}
      />

      <ConfirmDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
        title="Supprimer cette commission ?"
        description="Reste restaurable depuis la Corbeille."
        confirmLabel="Supprimer"
        variant="destructive"
        loading={actions.deletingId !== null}
        onConfirm={() => {
          if (pendingDeleteId === null) return;
          actions.deleteCommission(pendingDeleteId);
          setPendingDeleteId(null);
          pageState.setSelectedCommission(null);
        }}
      />
    </div>
  );
}
